/**
 * PrismLogo — Prismiq's hyperstylized prism identity mark.
 * A 3D-angled geometric prism with vibrant light-refraction gradient beams.
 * Adapts to light & dark UI themes via currentColor-friendly gradients.
 */
export default function PrismLogo({ size = 40, className = "", withGlow = true }) {
  const uid = `prism-${Math.random().toString(36).slice(2, 8)}`;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      data-testid="prism-logo"
      aria-label="Prismiq"
      role="img"
    >
      <defs>
        {/* Prism body — glassy indigo/violet gradient */}
        <linearGradient id={`${uid}-body`} x1="16" y1="8" x2="48" y2="56" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#818cf8" />
          <stop offset="55%" stopColor="#6366f1" />
          <stop offset="100%" stopColor="#4338ca" />
        </linearGradient>
        {/* Left facet — cyan sheen */}
        <linearGradient id={`${uid}-left`} x1="10" y1="20" x2="34" y2="56" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#a5f3fc" stopOpacity="0.95" />
          <stop offset="100%" stopColor="#22d3ee" stopOpacity="0.85" />
        </linearGradient>
        {/* Right facet — violet sheen */}
        <linearGradient id={`${uid}-right`} x1="34" y1="12" x2="58" y2="56" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#c4b5fd" stopOpacity="0.95" />
          <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0.9" />
        </linearGradient>
        {/* Refraction beams */}
        <linearGradient id={`${uid}-beam-1`} x1="44" y1="30" x2="62" y2="22" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#fef08a" stopOpacity="0.95" />
          <stop offset="100%" stopColor="#fef08a" stopOpacity="0" />
        </linearGradient>
        <linearGradient id={`${uid}-beam-2`} x1="44" y1="34" x2="62" y2="34" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#fca5a5" stopOpacity="0.95" />
          <stop offset="100%" stopColor="#fca5a5" stopOpacity="0" />
        </linearGradient>
        <linearGradient id={`${uid}-beam-3`} x1="44" y1="38" x2="62" y2="46" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#67e8f9" stopOpacity="0.95" />
          <stop offset="100%" stopColor="#67e8f9" stopOpacity="0" />
        </linearGradient>
        {/* Incoming white light beam */}
        <linearGradient id={`${uid}-in`} x1="2" y1="34" x2="20" y2="34" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0.95" />
        </linearGradient>
        {withGlow && (
          <filter id={`${uid}-glow`} x="-25%" y="-25%" width="150%" height="150%">
            <feGaussianBlur stdDeviation="1.2" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        )}
      </defs>

      {/* Incoming light beam */}
      <rect x="2" y="32.4" width="18" height="3.2" rx="1.6" fill={`url(#${uid}-in)`} />

      {/* Refraction beams (right) */}
      <rect x="44" y="28.6" width="20" height="2.4" rx="1.2" fill={`url(#${uid}-beam-1)`} transform="rotate(-14 44 29.8)" />
      <rect x="44" y="32.8" width="20" height="2.4" rx="1.2" fill={`url(#${uid}-beam-2)`} />
      <rect x="44" y="37" width="20" height="2.4" rx="1.2" fill={`url(#${uid}-beam-3)`} transform="rotate(14 44 38.2)" />

      {/* Prism body (triangle) with 3D facets */}
      <g filter={withGlow ? `url(#${uid}-glow)` : undefined}>
        {/* Back / main body */}
        <path d="M32 6 L54 50 L10 50 Z" fill={`url(#${uid}-body)`} />
        {/* Left front facet */}
        <path d="M32 6 L32 50 L10 50 Z" fill={`url(#${uid}-left)`} opacity="0.75" />
        {/* Right rim highlight */}
        <path d="M32 6 L54 50 L48 50 L32 18 Z" fill={`url(#${uid}-right)`} opacity="0.85" />
        {/* Sharp top edge highlight */}
        <path d="M32 6 L34 10 L30 10 Z" fill="#ffffff" opacity="0.9" />
      </g>

      {/* Crisp outline */}
      <path
        d="M32 6 L54 50 L10 50 Z"
        stroke="#ffffff"
        strokeOpacity="0.35"
        strokeWidth="0.8"
        strokeLinejoin="round"
      />
    </svg>
  );
}
